import {omitNullsy, serializeJSONToString, useApiQuery, useCurrentLocale} from '@wallaby-ui/core';
import {MerieSchema, useFormatters} from '@wallaby-ui/document';
import {useMemo} from 'react';

import {ChartDataType, InterventionOverviewReportFilter, ReportType, RowTypeWithTotal} from './types';

type UseReportDataProps = {
  reportUrl: string;
  filters: InterventionOverviewReportFilter;
  reportType: ReportType;
};

type UseReportDataReturn = {
  tableData: RowTypeWithTotal[];
  chartData: ChartDataType[];
  isLoading: boolean;
};

export const useReportData = ({reportUrl, filters, reportType}: UseReportDataProps): UseReportDataReturn => {
  const locale = useCurrentLocale();
  const {t} = useFormatters();

  const areAllFiltersSet = useMemo(() => {
    if (reportType === ReportType.NETWORK) {
      return (
        filters.dateFrom != null &&
        filters.dateTo != null &&
        filters.itemType != null &&
        filters.organizationLevelType != null &&
        filters.measureType != null
      );
    }
    return (
      filters.dateFrom != null &&
      filters.dateTo != null &&
      filters.itemType != null &&
      filters.organizationLevelType != null &&
      filters.measureType != null &&
      filters.organizationId != null
    );
  }, [reportType, filters]);

  const reportParams: MerieSchema.components['schemas']['InterventionOverviewReportFilter'] = useMemo(
    () =>
      omitNullsy({
        itemType: filters.itemType,
        measureType: filters.measureType,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        organizationId: filters.organizationId,
        organizationLevelType: filters.organizationLevelType,
        language: locale.toUpperCase(),
      }),
    [filters, locale]
  );

  const {data: reportData, isLoading} = useApiQuery<MerieSchema.components['schemas']['InterventionOverviewReportDto']>(
    [reportUrl, serializeJSONToString(reportParams)],
    null,
    {
      axiosConfig: {
        url: reportUrl,
        params: new URLSearchParams(reportParams as any),
      },
      reactQueryOptions: {
        enabled: areAllFiltersSet,
      },
    }
  );

  const tableData: RowTypeWithTotal[] = useMemo(() => {
    if (!reportData) return [];

    const rawData = reportData.organizationsStatistics ?? [];
    return [
      {
        organizationId: -1,
        organizationLabel: t('reports.totalRow'),
        hospCount: reportData.totalStatistics?.hospCount ?? 0,
        hospDaysCount: reportData.totalStatistics?.hospDaysCount ?? 0,
        uncheckedHospCount: reportData.totalStatistics?.uncheckedHospCount ?? 0,
        uncheckedHighRiskHospCount: reportData.totalStatistics?.uncheckedHighRiskHospCount ?? 0,
        measureCount: reportData.totalStatistics?.measureCount ?? 0,
        measureCountToHospCountPct: reportData.totalStatistics?.measureCountToHospCountPct ?? 0,
        measureCountPer1000HospDays: reportData.totalStatistics?.measureCountPer1000HospDays ?? 0,
        isTotalRow: true,
      },
      ...rawData,
    ];
  }, [reportData, t]);

  const testTableData: RowTypeWithTotal[] = useMemo(() => {
    if (!reportData) return [];

    const rawData = reportData.organizationsStatistics ?? [];
    return [
      {
        organizationId: -1,
        organizationLabel: t('reports.totalRow'),
        hospCount: 50000.66,
        hospDaysCount: 50000.66,
        uncheckedHospCount: 70000.66,
        uncheckedHighRiskHospCount: 80000.66,
        measureCount: 20000.66,
        measureCountToHospCountPct: 66.66,
        measureCountPer1000HospDays: 30000.66,
        isTotalRow: true,
      },
      ...rawData,
    ];
  }, [reportData, t]);

  const chartData: ChartDataType[] = useMemo(() => {
    if (!reportData?.organizationsChartStatistics) return [];

    return reportData.organizationsChartStatistics.map((item) => ({
      ...item,
      measureCountLow: item.measureCountLow ?? 0,
      measureCountLowPer1000HospDays: item.measureCountLowPer1000HospDays ?? 0,
      measureCountMedium: item.measureCountMedium ?? 0,
      measureCountMediumPer1000HospDays: item.measureCountMediumPer1000HospDays ?? 0,
      measureCountHigh: item.measureCountHigh ?? 0,
      measureCountHighPer1000HospDays: item.measureCountHighPer1000HospDays ?? 0,
    }));
  }, [reportData]);

  const testChartData: ChartDataType[] = useMemo(() => {
    if (!reportData?.organizationsChartStatistics) return [];

    return reportData.organizationsChartStatistics.map((item) => ({
      ...item,
      measureCountLow: 40000.66,
      measureCountLowPer1000HospDays: 40000.66,
      measureCountMedium: 20000.66,
      measureCountMediumPer1000HospDays: 20000.66,
      measureCountHigh: 10000.66,
      measureCountHighPer1000HospDays: 10000.66,
    }));
  }, [reportData]);

  return {
    tableData,
    chartData,
    isLoading,
  };
};
